import { observer } from 'mobx-react';
import styled from 'styled-components';
import { withStyles } from '@material-ui/core/styles';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
// import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpansionPanelActions from '@material-ui/core/ExpansionPanelDetails';
// import Button from '@material-ui/core/Button';
import ErrorIcon from '@material-ui/icons/Error';
// import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useStores } from '../../hooks/useStores';
import ImporterModal from '../ImporterModal';
import { Flex } from '../Flex';
import { readFile } from '../../utils/fileReader';
import theme from '../../utils/styles/theme';

const ErrorPanel = withStyles({
  root: {
    background: theme.colors.error[1],
    width: '100%',
  },
  details: {
    maxHeight: '200px',
    padding: theme.space[2],
  },
  errorList: {
    listStyle: 'inherit',
  },
  actions: {
    display: 'flex',
    flexDirection: 'row-reverse',
    width: '100%',
  },
})(({ classes, onClose, errors, helpLink }) => (
  <ExpansionPanel className={classes.root}>
    {/* <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>       */}
    <ExpansionPanelSummary>
      <ErrorIcon color="error" />
      <ErrorText>
        Unfortunately, we encountered an error while importing. To troubleshoot
        potential issues, refer to this{' '}
        <a href={helpLink} target="_blank" rel="noreferrer">
          help article
        </a>
        .
      </ErrorText>
    </ExpansionPanelSummary>
    {/* <ExpansionPanelDetails className={classes.details}>
      <ul className={classes.errorList}>
        {errors.map((error, index) => {
          return (
            <li key={index}>{error}</li>
          )
        })}
      </ul>
    </ExpansionPanelDetails> */}
    {/*
    <ExpansionPanelActions className={classes.actions}>
      <Button size="small" variant="text" color="inherit" onClick={onClose}>Dismiss</Button>
    </ExpansionPanelActions>
    */}
  </ExpansionPanel>
));

const ErrorContainer = styled(Flex)`
  background: ${({ theme }) => theme.colors.error[1]};
  width: 100%;
`;

const ErrorText = styled.span`
  margin-left: 10px;
`;

const ErrorBanner = ({ onClose, errors, helpLink }) => (
  <ErrorContainer>
    <ErrorPanel onClose={onClose} errors={errors} helpLink={helpLink} />
  </ErrorContainer>
);

const InstructionsContainer = styled(Flex).attrs(() => ({
  flexDirection: 'column',
}))`
  color: ${({ theme }) => theme.colors.blacks[4]};
  min-width: 240px;
  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    margin-right: 12px;
  }

  a {
    text-decoration: underline;
    color: ${({ theme }) => theme.colors.blacks[8]};
  }

  dl {
    font-size: ${({ theme }) => theme.fontSizes[2]};
    font-weight: ${({ theme }) => theme.fontWeights[5]};
    line-height: ${({ theme }) => theme.lineHeights.copy};
    display: flex;
    flex-flow: row;
    flex-wrap: wrap;
    width: 100%; /* set the container width*/
    overflow: visible;

    dt {
      flex: 0 0 50%;
      text-overflow: ellipsis;
      overflow: hidden;
    }
    dd {
      flex: 0 0 50%;
      margin-left: auto;
      text-align: left;
      text-overflow: ellipsis;
      overflow: hidden;
    }
  }
`;

const ProductImportModalContent = () => (
  <InstructionsContainer layout="column">
    <p>
      Download our sample CSV to use as a template for importing your new
      products. If you do not have the required extensions installed, the
      importer will install them for you.
    </p>
    <p>
      <a
        href={process.env.NEXT_PUBLIC_BULK_PRODUCT_IMPORT_SAMPLE_CSV}
        download="sample.csv"
      >
        Download our sample CSV template
      </a>
    </p>
    <dl>
      <dt>Maximum file size:</dt>
      <dd>5MB</dd>
      <dt>File type:</dt>
      <dd>Comma-separated values (.csv)</dd>
    </dl>
  </InstructionsContainer>
);

const ProductImportModal = () => {
  const { allProductsPage } = useStores();

  const onImportClicked = async (file) => {
    await allProductsPage.createProductImportJob();
    const fileContents = await readFile(file);
    await allProductsPage.uploadImportFile(fileContents);
    allProductsPage.setImportJobFileName(file.name);
    allProductsPage.startPollingImportJobStatus();
    allProductsPage.openProductImportProgressModal();
  };

  const onProductImportDialogClosed = () => {
    allProductsPage.clearProductImportJob();
    allProductsPage.closeProductImportModal();
  };

  return (
    allProductsPage.isImportModalOpen && (
      <ImporterModal
        title="Import Products"
        cancelText="Cancel"
        allowClose={false}
        helpLink={process.env.NEXT_PUBLIC_BULK_PRODUCT_IMPORT_HELP_URL}
        onImport={onImportClicked}
        dialogProps={{
          maxWidth: 'sm',
          open: allProductsPage.isImportModalOpen,
          onClose: onProductImportDialogClosed,
        }}
        errorComponent={
          allProductsPage.importerJobHasErrors && (
            <ErrorBanner
              onClose={allProductsPage.clearProductImportJob}
              errors={allProductsPage.importJob?.result?.formattedErrors}
              helpLink={process.env.NEXT_PUBLIC_BULK_PRODUCT_IMPORT_HELP_URL}
            />
          )
        }
        content={<ProductImportModalContent />}
      />
    )
  );
};

export default observer(ProductImportModal);
